import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Vendor Consent Landing Page
 * --------------------------------------------------------------------------
 * Public page (no auth). Vendor lands here from the soft pleasant invitation
 * letter. They can opt in (with pricing intake), opt out, or just read.
 *
 * Route: /vendor-consent/:token
 */
const VendorConsent = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // null | "opted_in" | "opted_out"

  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [pricing, setPricing] = useState([
    { service: "", price_min: 0, price_max: 0, unit: "service", notes: "" },
  ]);
  const [notes, setNotes] = useState("");
  const [confirmedNoMarkup, setConfirmedNoMarkup] = useState(false);
  const [confirmedOwnerDirect, setConfirmedOwnerDirect] = useState(false);
  const [confirmedNonInterference, setConfirmedNonInterference] = useState(false);
  const [confirmedInvitationLicense, setConfirmedInvitationLicense] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/vendor-consent/${token}`);
        const d = await r.json();
        if (!r.ok) {
          setError(d.detail || "Invitation link invalid or expired");
        } else {
          setInvite(d);
          if (d.already_responded) {
            setDone(d.status);
          }
        }
      } catch (e) {
        setError("Could not load invitation");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const addPricingRow = () => {
    setPricing([...pricing, { service: "", price_min: 0, price_max: 0, unit: "service", notes: "" }]);
  };
  const removePricingRow = (idx) => {
    setPricing(pricing.filter((_, i) => i !== idx));
  };
  const updatePricingRow = (idx, field, value) => {
    const next = [...pricing];
    next[idx] = { ...next[idx], [field]: field.startsWith("price_") ? parseFloat(value) || 0 : value };
    setPricing(next);
  };

  const submit = async (decision) => {
    if (decision === "opt_in") {
      if (!confirmedNoMarkup || !confirmedOwnerDirect || !confirmedNonInterference || !confirmedInvitationLicense) {
        alert("Please tick all four acknowledgements to opt in.");
        return;
      }
    }
    setSubmitting(true);
    try {
      const validPricing = decision === "opt_in"
        ? pricing.filter(p => p.service.trim() && (p.price_min > 0 || p.price_max > 0))
        : [];
      const r = await fetch(`${BACKEND_URL}/api/vendor-consent/${token}/respond`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          decision,
          pricing: validPricing,
          notes,
          contact_name: contactName,
          contact_phone: contactPhone,
          confirmed_no_markup: confirmedNoMarkup,
          confirmed_owner_direct: confirmedOwnerDirect,
          confirmed_non_interference: confirmedNonInterference,
          confirmed_invitation_license: confirmedInvitationLicense,
        }),
      });
      const d = await r.json();
      if (r.ok && d.ok) {
        setDone(d.status);
      } else {
        alert(d.detail || "Could not submit. Please reply to the email instead.");
      }
    } catch {
      alert("Network error. Please reply to the email instead.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div style={pageStyle}><div style={cardStyle}><p style={{ color: "#fff" }}>Loading...</p></div></div>;
  }

  if (error) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <h2 style={{ color: "#fca5a5" }}>Invitation Unavailable</h2>
          <p style={{ color: "#ADBED1" }}>{error}</p>
          <p style={{ color: "#ADBED1", marginTop: 14 }}>If you believe this is an error, reply to the email we sent or call <strong style={{ color: "#fff" }}>(817) 381-8831</strong>.</p>
        </div>
      </div>
    );
  }

  if (done === "opted_in") {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={{ fontSize: 56, textAlign: "center", marginBottom: 10 }}>🤝</div>
          <h2 style={{ color: "#00DC78", textAlign: "center" }}>You&apos;re In, {(invite.vendor_name || "Partner").split(" ")[0]}!</h2>
          <p style={{ color: "#ADBED1", lineHeight: 1.65 }}>
            Thank you for partnering with Moovin Forward. You&apos;ll start receiving owner introductions for jobs in your area as they come up.
          </p>
          <p style={{ color: "#ADBED1", lineHeight: 1.65 }}>
            <strong style={{ color: "#fff" }}>What to expect:</strong>
          </p>
          <ul style={{ color: "#ADBED1", lineHeight: 1.7, paddingLeft: 20 }}>
            <li>Owners will reach out to you <strong style={{ color: "#fff" }}>directly</strong> — never through us.</li>
            <li>Your contract, your scope, your invoice, your payment terms.</li>
            <li>We never collect a fee from you.</li>
            <li>You can opt out any time by replying to any email or calling Shannon at (817) 381-8831.</li>
          </ul>
          <p style={{ color: "#5AC8D8", marginTop: 18 }}>Welcome to the network.</p>
        </div>
      </div>
    );
  }

  if (done === "opted_out") {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={{ fontSize: 56, textAlign: "center", marginBottom: 10 }}>🙏</div>
          <h2 style={{ color: "#fff", textAlign: "center" }}>Thank you for letting us know.</h2>
          <p style={{ color: "#ADBED1", lineHeight: 1.65, textAlign: "center" }}>
            We&apos;ve removed your firm from the directory. We won&apos;t contact you about this again. Best of luck with everything.
          </p>
          <p style={{ color: "#7E92A8", textAlign: "center", marginTop: 18, fontSize: 13 }}>
            — Shannon &amp; the Moovin Forward team
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={{ ...cardStyle, maxWidth: 660 }} data-testid="vendor-consent-page">
        {/* Hero */}
        <div style={{ textAlign: "center", marginBottom: 18 }}>
          <div style={{ fontSize: 38, marginBottom: 6 }}>🤝</div>
          <p style={{ color: "#5AC8D8", fontSize: 13, fontWeight: 800, letterSpacing: "0.10em", textTransform: "uppercase", margin: "0 0 4px" }}>Moovin Forward LLC · DFW</p>
          <h1 style={{ color: "#fff", fontSize: 24, fontWeight: 800, margin: "0 0 6px", letterSpacing: "-0.01em" }}>
            Community Vendor Network
          </h1>
          <p style={{ color: "#ADBED1", fontSize: 14, margin: 0 }}>
            Invitation for <strong style={{ color: "#fff" }}>{invite.vendor_name}</strong>
          </p>
        </div>

        {/* Doctrine card */}
        <div style={{ padding: "14px 16px", background: "rgba(0,220,120,0.06)", border: "1px solid rgba(0,220,120,0.30)", borderRadius: 10, marginBottom: 18 }}>
          <h3 style={{ color: "#00DC78", fontSize: 14, fontWeight: 800, margin: "0 0 8px" }}>How this works</h3>
          <ul style={{ color: "#ADBED1", fontSize: 13, lineHeight: 1.65, margin: 0, paddingLeft: 18 }}>
            <li><strong style={{ color: "#fff" }}>Free.</strong> No fees, no commissions, no markup — ever.</li>
            <li><strong style={{ color: "#fff" }}>Opt-in.</strong> You control whether you&apos;re listed. Opt out any time.</li>
            <li><strong style={{ color: "#fff" }}>You set pricing.</strong> Share your typical ranges so owners come ready.</li>
            <li><strong style={{ color: "#fff" }}>Owner pays you direct.</strong> All fees, deposits, invoices — straight to your business.</li>
            <li><strong style={{ color: "#fff" }}>Your contract.</strong> We don&apos;t draft, negotiate, or sign anything between you and the owner.</li>
          </ul>
        </div>

        {/* Contact intake */}
        <h3 style={sectionStyle}>👤 Best contact at your firm</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10, marginBottom: 14 }}>
          <input type="text" value={contactName} onChange={e => setContactName(e.target.value)} placeholder="Your name" data-testid="vc-name" style={inputStyle} />
          <input type="tel" value={contactPhone} onChange={e => setContactPhone(e.target.value)} placeholder="Phone (optional)" data-testid="vc-phone" style={inputStyle} />
        </div>

        {/* Pricing intake */}
        <h3 style={sectionStyle}>💵 Your typical pricing</h3>
        <p style={{ color: "#7E92A8", fontSize: 12, margin: "0 0 10px", lineHeight: 1.5 }}>
          Give owners a realistic budget. Examples: "Pool chemical service · $150-$250 per visit" · "HVAC tune-up · $89-$129 per unit". Add as many lines as you want.
        </p>
        {pricing.map((p, idx) => (
          <div key={idx} style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
            <input type="text" placeholder="Service" value={p.service} onChange={e => updatePricingRow(idx, "service", e.target.value)} data-testid={`vc-pricing-service-${idx}`} style={{ ...inputStyle, flex: "2 1 200px" }} />
            <input type="number" placeholder="$ min" value={p.price_min || ""} onChange={e => updatePricingRow(idx, "price_min", e.target.value)} data-testid={`vc-pricing-min-${idx}`} style={{ ...inputStyle, width: 80, flex: "0 0 80px" }} />
            <input type="number" placeholder="$ max" value={p.price_max || ""} onChange={e => updatePricingRow(idx, "price_max", e.target.value)} data-testid={`vc-pricing-max-${idx}`} style={{ ...inputStyle, width: 80, flex: "0 0 80px" }} />
            <select value={p.unit} onChange={e => updatePricingRow(idx, "unit", e.target.value)} data-testid={`vc-pricing-unit-${idx}`} style={{ ...inputStyle, width: 110, flex: "0 0 110px" }}>
              <option value="service" style={{ background: "#030B1A" }}>per service</option>
              <option value="visit" style={{ background: "#030B1A" }}>per visit</option>
              <option value="hour" style={{ background: "#030B1A" }}>per hour</option>
              <option value="unit" style={{ background: "#030B1A" }}>per unit</option>
              <option value="sqft" style={{ background: "#030B1A" }}>per sq ft</option>
              <option value="flat" style={{ background: "#030B1A" }}>flat</option>
            </select>
            {pricing.length > 1 && (
              <button onClick={() => removePricingRow(idx)} data-testid={`vc-pricing-remove-${idx}`} style={{ background: "transparent", color: "#fca5a5", border: "1px solid rgba(252,165,165,0.30)", borderRadius: 6, padding: "0 10px", cursor: "pointer", fontSize: 13 }}>✕</button>
            )}
          </div>
        ))}
        <button onClick={addPricingRow} data-testid="vc-pricing-add" style={{ background: "transparent", color: "#5AC8D8", border: "1px dashed rgba(0,255,255,0.30)", borderRadius: 6, padding: "8px 14px", cursor: "pointer", fontSize: 13, fontWeight: 700, marginTop: 4 }}>
          + Add another service
        </button>

        {/* Notes */}
        <h3 style={sectionStyle}>💬 Anything else?</h3>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional — service area limits, scheduling preferences, certifications, etc." rows={3} data-testid="vc-notes" style={{ ...inputStyle, fontFamily: "inherit" }} />

        {/* Acknowledgements (required for opt-in) */}
        <div style={{ marginTop: 18, padding: "12px 14px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.10)", borderRadius: 8 }}>
          <p style={{ color: "#ADBED1", fontSize: 12, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", margin: "0 0 8px" }}>Quick acknowledgements (required to opt in)</p>
          <label style={ackLabel} data-testid="vc-ack-markup-label">
            <input type="checkbox" checked={confirmedNoMarkup} onChange={e => setConfirmedNoMarkup(e.target.checked)} data-testid="vc-ack-markup" style={{ marginTop: 3 }} />
            <span style={{ color: "#fff", fontSize: 13, lineHeight: 1.5 }}>I understand <strong>Moovin Forward does NOT collect any fee, commission, or markup</strong> from my work.</span>
          </label>
          <label style={ackLabel} data-testid="vc-ack-direct-label">
            <input type="checkbox" checked={confirmedOwnerDirect} onChange={e => setConfirmedOwnerDirect(e.target.checked)} data-testid="vc-ack-direct" style={{ marginTop: 3 }} />
            <span style={{ color: "#fff", fontSize: 13, lineHeight: 1.5 }}>I understand <strong>property owners contract with my firm directly</strong> — Moovin Forward does not draft, sign, or hold any interest in our contracts.</span>
          </label>
          <label style={ackLabel} data-testid="vc-ack-noninterference-label">
            <input type="checkbox" checked={confirmedNonInterference} onChange={e => setConfirmedNonInterference(e.target.checked)} data-testid="vc-ack-noninterference" style={{ marginTop: 3 }} />
            <span style={{ color: "#fff", fontSize: 13, lineHeight: 1.5 }}><strong>Auxiliary / Non-Exclusive Status:</strong> I am being listed as an auxiliary, non-exclusive option only. I will not knowingly interfere with, displace, or solicit work away from any existing primary contractor already engaged on a property.</span>
          </label>
          <label style={ackLabel} data-testid="vc-ack-license-label">
            <input type="checkbox" checked={confirmedInvitationLicense} onChange={e => setConfirmedInvitationLicense(e.target.checked)} data-testid="vc-ack-license" style={{ marginTop: 3 }} />
            <span style={{ color: "#fff", fontSize: 13, lineHeight: 1.5 }}><strong>Limited, Revocable Invitation License:</strong> Any public-directory information about my firm referenced by Moovin Forward is used solely under a limited, revocable invitation license. I may revoke this license at any time by replying "remove" — no copyright, trademark, or brand claim is implied.</span>
          </label>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 10, marginTop: 22, flexWrap: "wrap" }}>
          <button
            onClick={() => submit("opt_in")}
            disabled={submitting || !confirmedNoMarkup || !confirmedOwnerDirect || !confirmedNonInterference || !confirmedInvitationLicense}
            data-testid="vc-opt-in-btn"
            style={{
              flex: 2, minWidth: 200, padding: "14px 18px", borderRadius: 8, border: "none",
              background: (confirmedNoMarkup && confirmedOwnerDirect && confirmedNonInterference && confirmedInvitationLicense && !submitting) ? "linear-gradient(135deg, #00DC78 0%, #00FFFF 100%)" : "rgba(255,255,255,0.10)",
              color: (confirmedNoMarkup && confirmedOwnerDirect && confirmedNonInterference && confirmedInvitationLicense && !submitting) ? "#030B1A" : "#7E92A8",
              fontSize: 15, fontWeight: 800, cursor: (confirmedNoMarkup && confirmedOwnerDirect && confirmedNonInterference && confirmedInvitationLicense && !submitting) ? "pointer" : "not-allowed",
            }}
          >
            ✓ Opt In to the Network
          </button>
          <button onClick={() => submit("opt_out")} disabled={submitting} data-testid="vc-opt-out-btn" style={{
            flex: 1, minWidth: 140, padding: "14px 18px", borderRadius: 8,
            border: "1px solid rgba(255,255,255,0.20)", background: "transparent",
            color: "#ADBED1", fontSize: 13, fontWeight: 700, cursor: "pointer",
          }}>
            No thanks
          </button>
        </div>

        {/* Footer */}
        <p style={{ color: "#7E92A8", fontSize: 11, textAlign: "center", marginTop: 22, lineHeight: 1.5 }}>
          Moovin Forward LLC is an interior unit stabilization firm. The Community Vendor Network is an informational directory only — not real estate brokerage, property management, or leasing. Questions? Email <a href="mailto:hello@moovinforward.org" style={{ color: "#5AC8D8" }}>hello@moovinforward.org</a> or call (817) 381-8831.
        </p>
      </div>
    </div>
  );
};

const pageStyle = { minHeight: "100vh", background: "linear-gradient(135deg, #030B1A 0%, #0a1929 100%)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "32px 16px" };
const cardStyle = { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(0,255,255,0.20)", borderRadius: 14, padding: "28px 26px", maxWidth: 540, width: "100%" };
const sectionStyle = { color: "#fff", fontSize: 14, fontWeight: 800, margin: "16px 0 8px", letterSpacing: "-0.01em" };
const inputStyle = { padding: "10px 12px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", color: "#fff", fontSize: 14, boxSizing: "border-box", outline: "none", width: "100%" };
const ackLabel = { display: "flex", alignItems: "flex-start", gap: 8, padding: "6px 0", cursor: "pointer" };

export default VendorConsent;
